import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { APPLICATION_STATUS, FIRM_APPLICATION_TYPES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { FirmService } from 'app/services/firms/firm.service';
import { ToastrService } from 'ngx-toastr';
import { FirmApplicationComponent } from '../firm-application/firm-application.component';

@Component({
  selector: 'app-firm-application-list',
  templateUrl: './firm-application-list.component.html',
  styleUrls: ['./firm-application-list.component.scss']
})
export class FirmApplicationListComponent implements OnInit, OnChanges {
  @Input() firmId: number | null = null;
  @Input() isEmbedded = false;
  @Output() countChanged = new EventEmitter<number>();

  public isMobile = false;
  public loading = true;
  public pageSize = 50;
  public uiData = LEAD_BASED_PAINT;
  public applications: IFirmApplication[] = [];
  public filteredApplications: IFirmApplication[] = [];
  public dataSource = new MatTableDataSource<IFirmApplication>([]);
  public filterString = '';
  public selectedStatus = new FormControl([]);
  public selectedType = new FormControl([]);
  public applicationDateFilterStart = new FormControl(null);
  public applicationDateFilterEnd = new FormControl(null);
  public firmApplicationTable = {
    icon: 'assignment',
    title: 'Firm Applications',
    layout: {
      columns: ['applicationType', 'firmName', 'applicationStatus', 'comments', 'applicationDate', 'approvalDate'],
      container: [
        { displayName: 'Application Type', columnName: 'applicationType', type: 'string', size: '16' },
        { displayName: 'Firm Name', columnName: 'firmName', type: 'string', size: '23' },
        { displayName: 'Status', columnName: 'applicationStatus', type: 'status', size: '11' },
        { displayName: 'Comments', columnName: 'comments', type: 'string', size: '18' },
        { displayName: 'Application Date', columnName: 'applicationDate', type: 'date', size: '11' },
        { displayName: 'Status Date', columnName: 'approvalDate', type: 'date', size: '11' },
      ],
    },
  };

  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private commonService: CommonService,
    private firmService: FirmService,
    public sharedService: SharedService,
    private toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['firmId'] && !changes['firmId'].firstChange) {
      this.loadData();
    }
  }

  get tableColumns(): string[] {
    return this.firmApplicationTable.layout.columns.concat('actions');
  }

  loadData(): void {
    this.loading = true;

    if (!this.isEmbedded) {
      this.filterString = this.commonService.firmApplicationFilter.filterString;
      this.selectedStatus = this.commonService.firmApplicationFilter.selectedStatus;
      this.selectedType = this.commonService.firmApplicationFilter.selectedType;
      this.applicationDateFilterStart.setValue(this.commonService.firmApplicationFilter.applicationDateFilterStart);
      this.applicationDateFilterEnd.setValue(this.commonService.firmApplicationFilter.applicationDateFilterEnd);
    }

    this.firmService.getFirmApplications(this.firmId).subscribe({
      next: (applications) => {
        this.applications = applications;
        this.loading = false;
        this.filterTable();
      },
      error: (error) => {
        this.toastr.error('Error loading Firm applications: ' + error.message);
        this.loading = false;
      }
    });
  }

  filterTable(): void {
    if (!this.isEmbedded) {
      this.commonService.firmApplicationFilter.filterString = this.filterString;
      this.commonService.firmApplicationFilter.selectedStatus = this.selectedStatus;
      this.commonService.firmApplicationFilter.selectedType = this.selectedType;
      this.commonService.firmApplicationFilter.applicationDateFilterStart = this.applicationDateFilterStart.value;
      this.commonService.firmApplicationFilter.applicationDateFilterEnd = this.applicationDateFilterEnd.value;
    }

    this.filteredApplications = this.applications.filter((application) => {
      const text = this.filterString.trim().toLowerCase();
      const matchesText = !text || [
        application.id.toString(),
        application.firmName,
        application.ccb || '',
        this.getCommentsText(application),
      ].join(' ').toLowerCase().includes(text);

      const statusValues = this.selectedStatus.value || [];
      const matchesStatus = !statusValues.length || statusValues.includes(this.getDisplayStatus(application));

      const typeValues = this.selectedType.value || [];
      const matchesType = !typeValues.length || typeValues.includes(application.applicationType);

      const applicationDate = application.applicationDate ? this.toDateOnly(application.applicationDate) : null;
      const filterStart = this.applicationDateFilterStart.value ? this.toDateOnly(this.applicationDateFilterStart.value) : null;
      const filterEnd = this.applicationDateFilterEnd.value ? this.toDateOnly(this.applicationDateFilterEnd.value) : null;
      const matchesDate = (!filterStart && !filterEnd)
        || (!!applicationDate
          && (!filterStart || applicationDate >= filterStart)
          && (!filterEnd || applicationDate <= filterEnd));

      return matchesText && matchesStatus && matchesType && matchesDate;
    });

    this.dataSource = new MatTableDataSource<IFirmApplication>(this.filteredApplications);
    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    });

    this.countChanged.emit(this.filteredApplications.length);
  }

  clearFilters(): void {
    this.filterString = '';
    this.selectedStatus.setValue(['In Progress', 'Submitted', 'In Review', 'Action Needed', 'Expired']);
    this.selectedType.setValue([]);
    this.applicationDateFilterStart.reset();
    this.applicationDateFilterEnd.reset();
    this.filterTable();
  }

  getStatusValues(): string[] {
    return Object.values(APPLICATION_STATUS).concat('Expired');
  }

  getApplicationTypeValues(): string[] {
    return Object.values(FIRM_APPLICATION_TYPES);
  }

  getApplicationTypeLabel(applicationType: string): string {
    const value = (applicationType || '').toLowerCase();
    if (value === FIRM_APPLICATION_TYPES.rrp) {
      return 'RRP';
    }
    if (value === FIRM_APPLICATION_TYPES.lbpa) {
      return 'LBPA';
    }
    if (value === FIRM_APPLICATION_TYPES.link) {
      return 'Account Link';
    }
    return applicationType || 'Unknown';
  }

  getColumnText(application: IFirmApplication, columnName: string): string {
    switch (columnName) {
      case 'applicationType':
        return this.getApplicationTypeLabel(application.applicationType);
      case 'firmName':
        return application.firmName || 'Not provided';
      case 'comments':
        return this.getCommentsText(application);
      case 'ccb':
        return application.ccb || application.previousApplication || 'Not provided';
      default: {
        const value = application[columnName as keyof IFirmApplication];
        return value === null || value === undefined || value === '' ? 'Not provided' : value.toString();
      }
    }
  }

  openStartApplication(): void {
    if (this.isEmbedded && this.firmId) {
      const existingApplication = this.getBlockingInProcessFirmTypeApplication();
      if (existingApplication) {
        const typeLabel = this.getApplicationTypeLabel(existingApplication.applicationType);
        this.toastr.warning(
          'A valid ' + typeLabel + ' application is already in progress. Opening the existing application instead.',
          'Application Already In Progress'
        );
        this.openApplication(existingApplication);
        return;
      }

      this.openApplicationDialog({
        firmId: this.firmId,
        workflow: 'new',
      });
      return;
    }

    this.openApplicationDialog({});
  }

  openApplication(application: IFirmApplication): void {
    this.openApplicationDialog({ applicationId: application.id });
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  isReadOnlyApplication(application: IFirmApplication): boolean {
    return !['In Progress', 'Action Needed'].includes(this.getDisplayStatus(application));
  }

  getDisplayStatus(application: IFirmApplication): string {
    return this.isApplicationExpired(application) ? 'Expired' : application.applicationStatus;
  }

  getDateColumnValue(application: IFirmApplication, columnName: string): string | null {
    if (columnName === 'approvalDate') {
      return this.getStatusDate(application);
    }

    const value = application[columnName as keyof IFirmApplication];
    return typeof value === 'string' ? value : value ? value.toString() : null;
  }

  isCommentsColumn(columnName: string): boolean {
    return columnName === 'comments';
  }

  private getCommentsText(application: IFirmApplication): string {
    if (!this.firmService.shouldDisplayApplicationDeficiencies(application)) {
      return '';
    }

    return this.firmService.getUnresolvedApplicationDeficiencyText(application);
  }

  private getStatusDate(application: IFirmApplication): string | null {
    const status = this.normalizeStatus(application.applicationStatus);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return application.approvalDate || null;
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return application.lastUpdated || null;
    }

    return null;
  }

  private isApplicationExpired(application: IFirmApplication): boolean {
    if (!application.applicationDate) {
      return false;
    }

    const status = this.normalizeStatus(application.applicationStatus);
    if (status === this.normalizeStatus(APPLICATION_STATUS.approved) || status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return false;
    }

    const currentDate = this.toDateOnly(new Date());
    const applicationDate = this.toDateOnly(application.applicationDate);
    const diffInDays = (currentDate.getTime() - applicationDate.getTime()) / (1000 * 3600 * 24);
    return diffInDays > 90;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private getBlockingInProcessFirmTypeApplication(): IFirmApplication | undefined {
    return this.applications
      .filter((application) => {
        const type = this.normalizeStatus(application.applicationType);
        return type === this.normalizeStatus(FIRM_APPLICATION_TYPES.lbpa)
          || type === this.normalizeStatus(FIRM_APPLICATION_TYPES.rrp);
      })
      .filter((application) => {
        const status = this.normalizeStatus(application.applicationStatus);
        return status !== this.normalizeStatus(APPLICATION_STATUS.approved)
          && status !== this.normalizeStatus(APPLICATION_STATUS.cancelled);
      })
      .filter((application) => !this.isApplicationExpired(application))
      .sort((left, right) => {
        const leftDate = new Date(left.applicationDate || 0).getTime();
        const rightDate = new Date(right.applicationDate || 0).getTime();
        return rightDate - leftDate;
      })[0];
  }

  private openApplicationDialog(data: { applicationId?: number; firmId?: number | null; workflow?: 'new' | 'link' }): void {
    const dialogRef = this.dialog.open(FirmApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
      }
    });
  }

  private toDateOnly(value: string | Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}